// Garde et rendu du studio : tout contenu reste du texte React échappé.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { VersionTexteStudio } from '@artosera/shared';
import { useQuery } from '@tanstack/react-query';
import { iaApi } from '../../api/ia.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { galerieALaction } from '../../auth/sujet.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import styles from './Studio.module.css';

export function VersionStudio({ version }: { version: VersionTexteStudio }): JSX.Element {
  const { t } = useTranslation('regie');
  return (
    <article className={styles.texte} aria-label={t('studio.version', { n: version.n })}>
      {version.contenu.titre && <h2>{version.contenu.titre}</h2>}
      {version.contenu.chapo && <p>{version.contenu.chapo}</p>}
      <p>{version.contenu.corps}</p>
      {version.aVerifier.length > 0 && (
        <aside>
          <h3>{t('studio.aVerifier')}</h3>
          <ul>
            {version.aVerifier.map((v, i) => (
              <li key={i}>{v}</li>
            ))}
          </ul>
        </aside>
      )}
    </article>
  );
}

export function useDisponibiliteStudio(
  usage: 'studio:texte' | 'studio:traduction' = 'studio:texte',
) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const etat = useQuery({
    queryKey: cleMetier(galerieSlug, 'ia', 'etat'),
    queryFn: (c) => iaApi.etat(galerieSlug, appelDeRequete(c)),
    enabled: galerieALaction(galerie, 'ia:read'),
    refetchInterval: 30000,
  });
  const raison = etat.isError
    ? t('studio.iaIndisponible')
    : !etat.data
      ? null
      : etat.data.disponibilite !== 'active'
        ? t(`ia.disponibilites.${etat.data.disponibilite}`)
        : !etat.data.usagesPermis.includes(usage)
          ? t('ia.erreurs.coupee')
          : etat.data.consommation.partUtilisee >= 1
            ? t('ia.erreurs.plafond')
            : null;
  return { raison, disponible: !!etat.data && !etat.isError && raison === null };
}

export function erreurStudio(e: unknown, t: (cle: string) => string) {
  if (estApiError(e)) {
    if (e.status === 409) return t('studio.conflit');
    const raison =
      e.details && typeof e.details === 'object' && 'raison' in e.details ? e.details.raison : null;
    if (typeof raison === 'string' && raison.startsWith('ia_'))
      return t(`ia.erreurs.${raison.slice(3)}`);
    return e.message;
  }
  return t('studio.erreur');
}
